import React, {Fragment, useEffect, useState} from 'react';
import PropTypes from "prop-types";

import ModalContentWrapper from '../ModalContentWrapper';
import '../TwoColumnModal.scss';
import SpinnerDiv from "root/Forms/UI/SpinnerDiv";
import {useForm} from "react-hook-form";
import DropdownChildConnections from "root/ReactHookForm/ui/DropdownChildConnections";
import DropdownCommsPlanTemplates from "root/ReactHookForm/ui/DropdownCommsPlanTemplates";
import {createCommunicationsPlansForAdoptee, getCommunicationsPlanDetails, updateCommunicationsPlanDetails} from "root/DataFunctions/CommunicationsPlanDataFns";
import DatePickerControl from "root/ReactHookForm/ControlWrappers/DatePickerControl";
import {ModalCancelButton, ModalSaveButton} from "root/Modals/buttons";
import {getDataFromResponse} from "root/DataFunctions/BaseDataFns";
import parse from "date-fns/parse";
import format from "date-fns/format";

export const ModalCommunicationPlanModes = {
    CREATE: 'create',
    FINALISE: 'finalise',
    EDIT: 'edit',
    DELETE: 'delete',
    REOPEN: 'reopen'
}

const ReactHookFormError = ({formErrors, name}) =>
{
    const style = {marginTop: '5px', color: 'red'};

    return (
        <Fragment>
            {formErrors.user !== undefined && formErrors.user[name] && formErrors.user[name].type === "validate" &&
                <div style={style}>{formErrors.user[name].message}</div>}
            {formErrors.user !== undefined && formErrors.user[name] && formErrors.user[name].type === "required" &&
                <div style={style}>This is required</div>}
            {formErrors.user !== undefined && formErrors.user[name] && formErrors.user[name].type === "maxLength" &&
                <div style={style}>Max length exceeded</div>}
        </Fragment>
    )
}

const ModalCommunicationPlan = ({child_uid, communications_plan_id, onSaveCompleted, onClose, mode, width}) =>
{
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const shouldFetch = [ModalCommunicationPlanModes.FINALISE, ModalCommunicationPlanModes.DELETE, ModalCommunicationPlanModes.REOPEN, ModalCommunicationPlanModes.EDIT].includes(mode);

    const [data, setData] = useState({isLoading: shouldFetch, data: []});
    const [fromUserName, setFromUserName] = useState('');

    const {register, control, handleSubmit, setFocus, watch, setValue, formState: {errors: formErrors}} = useForm(
        {
            defaultValues: {
                user: {
                    connected_user: null,
                    agreement_date: null,
                    template: null,
                }
            }
        });

    useEffect(() =>
    {
        if (shouldFetch)
        {
            getCommunicationsPlanDetails(child_uid, communications_plan_id, false, (response) =>
            {
                const planData = getDataFromResponse(response)[0];

                const aDate = planData.agreement_date;

                const connections = planData.connections;

                const agreementDate = aDate === null ? null : parse(aDate, 'dd/MM/yyyy', new Date());

                const values = connections.map((conn)=>{
                    return {value: +conn.ur_id}
                })

                setValue('user', {
                        connected_user: values,
                        agreement_date: agreementDate,
                        template: {value: planData.cp_template_id},
                        loaded: true,
                    }
                );

                setData({isLoading: false, data: planData});
            });
        }
        else
        {
            setValue('user', {
                    connected_user: null,
                    agreement_date: null,
                    template: null,
                    loaded: true,
                }
            );
            setData({isLoading: false, data: []});
        }
    }, []);

    const onSaveComplete = () =>
    {
        onSaveCompleted('saved');
        onClose();
    }

    const onClickCancel = () =>
    {
        onClose();
    }

    const doSaveNew = (data) =>
    {
        createCommunicationsPlansForAdoptee(child_uid, data, (response) =>
        {
            onSaveComplete();
        });
    }

    const doUpdateAgreement = (data) =>
    {
        updateCommunicationsPlanDetails(child_uid, communications_plan_id, data, (response) =>
        {
            onSaveComplete();
        });
    }

    const disableButtons = () =>
    {
        setButtonDisabled(true);
    }

    const getSaveButtonText = () =>
    {
        const saveButtonText = {
            [ModalCommunicationPlanModes.CREATE]: 'Save',
            [ModalCommunicationPlanModes.EDIT]: 'Save',
            [ModalCommunicationPlanModes.FINALISE]: 'Finalise',
            [ModalCommunicationPlanModes.DELETE]: 'Delete',
            [ModalCommunicationPlanModes.REOPEN]: 'Reopen',
        };

        return saveButtonText[mode] === undefined ? 'Action' : saveButtonText[mode];
    }

    const transformData = (formData) =>
    {
        const returnData = {
            agreement_date: formData.user.agreement_date !== null ? format(formData.user.agreement_date, 'dd/MM/yyyy') : null,
            connected_user: formData.user.connected_user,
            template: formData.user.template
        }

        return returnData;
    }

    const onSubmit = (data) =>
    {
        disableButtons();

        switch (mode)
        {
            case ModalCommunicationPlanModes.CREATE :
                doSaveNew(transformData(data), onSaveComplete);
                break;
            case ModalCommunicationPlanModes.FINALISE :
                const updateData = {...transformData(data), mode: ModalCommunicationPlanModes.FINALISE};
                doUpdateAgreement(updateData, onSaveComplete);
                break;
            case ModalCommunicationPlanModes.REOPEN :
                const reopenData = {...transformData(data), mode: ModalCommunicationPlanModes.REOPEN};
                doUpdateAgreement(reopenData, onSaveComplete);
                break;
            case ModalCommunicationPlanModes.DELETE :
                const deleteData = {...transformData(data), mode: ModalCommunicationPlanModes.DELETE};
                doUpdateAgreement(deleteData, onSaveComplete);
                break;
        }
    }

    const onClickSaveForm = (status) =>
    {
        handleSubmit(onSubmit)(status);
    }

    const validateConnectionDropdown = (val) =>
    {
        if (val === null)
        {
            return "You must select a connection";
        }
    }

    const validateTemplateDropdown = (val) =>
    {
        if (val === null)
        {
            return "You must select a template";
        }
    }

    const validateAgreementDate = (val) =>
    {
        if (val === null && mode === ModalCommunicationPlanModes.FINALISE)
        {
            return "You must select an agreement date";
        }
    }

    const createFormContent = () =>
    {
        if (data.isLoading === true)
        {
            return (
                <div style={{width: width}}>
                    <SpinnerDiv/>
                </div>
            )
        }
        else
        {
            const sliderGroupStyle = {
                display: 'grid',
                gridTemplateColumns: '4fr 1fr',
                marginTop: '15px',
                fontSize: 'larger'
            }

            const dropdownIsReadOnly = mode !== ModalCommunicationPlanModes.EDIT && mode !== ModalCommunicationPlanModes.CREATE;
            const isCommunicationFlagDropdownFlag = !dropdownIsReadOnly;
            const dateReadOnly = mode === ModalCommunicationPlanModes.DELETE || mode === ModalCommunicationPlanModes.REOPEN ;

            return (
                <Fragment>

                    <div className='buy-card-right'>

                        <form className='registration-form' onSubmit={handleSubmit(onSubmit)}>

                            <div style={{marginBottom: '10px'}}>
                                <label>Connected User</label>
                                <DropdownChildConnections readOnly={dropdownIsReadOnly} control={control}
                                                          mode={mode} name='user.connected_user'
                                                          rules={{required: true, validate: validateConnectionDropdown}}
                                                          child_uid={child_uid} placeholder='Please select User'
                                                          isSearchable={true} isCommunicationsPlan={true}/>
                                <ReactHookFormError formErrors={formErrors} name='connected_user'/>
                            </div>
                            <div style={{marginBottom: '10px'}}>
                                <label>Agreement Date</label>
                                <DatePickerControl name='user.agreement_date' control={control}
                                                   validate={validateAgreementDate} placeHolder='Please Select Date'
                                                   readOnly={dateReadOnly}/>
                                <ReactHookFormError formErrors={formErrors} name='agreement_date'/>
                            </div>
                            <div style={{marginBottom: '10px'}}>
                                <label>Communications Plan Type</label>
                                <DropdownCommsPlanTemplates readOnly={dropdownIsReadOnly} control={control}
                                                            mode={mode} name='user.template'
                                                            rules={{required: true, validate: validateTemplateDropdown}}
                                                            organisation_id={'user'}
                                                            placeholder='Please select a template'
                                                            isSearchable={true}/>
                                <ReactHookFormError formErrors={formErrors} name='template'/>
                            </div>
                        </form>
                    </div>

                    <div className="d-flex flex-row justify-content-end">
                        <ModalCancelButton onClick={onClickCancel}>Cancel</ModalCancelButton>
                        <ModalSaveButton buttonDisabled={buttonDisabled}
                                         onClick={onClickSaveForm}>{getSaveButtonText(mode)}</ModalSaveButton>
                    </div>

                </Fragment>
            )
        }
    }

    const title = () =>
    {
        const headerStyle = {marginBottom: '10px'};

        switch (mode)
        {
            case ModalCommunicationPlanModes.CREATE:
                return <h3 style={headerStyle}>{'New Communications Plan With ' + fromUserName}</h3>
            case ModalCommunicationPlanModes.FINALISE:
                return <h3 style={headerStyle}>{'Finalise ' + fromUserName}</h3>
            case ModalCommunicationPlanModes.EDIT:
                return <h3 style={headerStyle}>{'Edit ' + fromUserName}</h3>
            case ModalCommunicationPlanModes.REOPEN:
                return <h3 style={headerStyle}>{'Re-open ' + fromUserName}</h3>
            case ModalCommunicationPlanModes.DELETE:
                return <h3 style={headerStyle}>{'Delete ' + fromUserName}</h3>
        }
    }

    return (
        <ModalContentWrapper className="modal-wrapper" width={'600px'} columns={2}>
            {title()}
            {data.isLoading === false &&
                <Fragment>
                    {mode === ModalCommunicationPlanModes.CREATE && createFormContent()}
                    {mode === ModalCommunicationPlanModes.FINALISE && createFormContent()}
                    {mode === ModalCommunicationPlanModes.REOPEN && createFormContent()}
                    {mode === ModalCommunicationPlanModes.DELETE && createFormContent()}
                    {mode === ModalCommunicationPlanModes.EDIT && createFormContent()}
                </Fragment>}
        </ModalContentWrapper>
    )
}

export default ModalCommunicationPlan;

ModalCommunicationPlan.propTypes = {
    params: PropTypes.object,
    user_id: PropTypes.string,
    width: PropTypes.number,
    mode: PropTypes.oneOf(Object.values(ModalCommunicationPlanModes))
};

ModalCommunicationPlan.defaultProps = {
    width: '400px'
}